import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiOutlineUser, HiOutlineMail, HiOutlinePhone, HiOutlineLocationMarker } from "react-icons/hi";
import { BiMessageDetail } from "react-icons/bi";
import { FiSend, FiCheck } from "react-icons/fi";
import { BsClock } from "react-icons/bs";

// Enhanced Sparkle component
const Sparkle = ({ size = 6, x = 0, y = 0, delay = 0 }) => (
  <motion.div
    className="absolute bg-white rounded-full opacity-60 hidden lg:block pointer-events-none"
    style={{
      width: size,
      height: size,
      top: `${y}%`,
      left: `${x}%`,
      boxShadow: "0 0 12px 3px rgba(255,255,255,0.6)",
    }}
    initial={{ scale: 0, opacity: 0 }}
    animate={{
      scale: [0, 1.2, 0],
      opacity: [0, 0.8, 0],
      y: [0, -15, 0],
    }}
    transition={{
      duration: 3 + Math.random() * 2,
      repeat: Infinity,
      delay,
      ease: "easeInOut"
    }}
  />
);

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: "", email: "", message: "" });
    }, 3000);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { 
        staggerChildren: 0.15, 
        delayChildren: 0.1 
      } 
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    },
  };

  const contactInfo = [
    {
      icon: <HiOutlineLocationMarker className="w-6 h-6" />,
      title: "Visit Us",
      content: "Doko Road, Bida, Niger State, Nigeria",
      link: "https://maps.google.com",
      color: "blue",
    },
    {
      icon: <HiOutlinePhone className="w-6 h-6" />,
      title: "Call Us",
      content: "+234 813 864 4342",
      link: "tel:+2348138644342",
      color: "green",
    },
    {
      icon: <HiOutlineMail className="w-6 h-6" />,
      title: "Email Us",
      content: "info@fedpolybida.edu.ng",
      link: "mailto:info@fedpolybida.edu.ng",
      color: "purple",
    },
    {
      icon: <BsClock className="w-6 h-6" />,
      title: "Working Hours",
      content: "Mon - Fri: 8:00 AM - 4:00 PM",
      color: "orange",
    },
  ];

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 overflow-hidden">
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs */}
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400/20 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Sparkles */}
        {Array.from({ length: 15 }).map((_, i) => (
          <Sparkle
            key={i}
            size={Math.random() * 4 + 3}
            x={Math.random() * 100}
            y={Math.random() * 100}
            delay={Math.random() * 5}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header Section */}
          <motion.div
            className="text-center mb-10 sm:mb-12 md:mb-16"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
            >
              Get In Touch
            </motion.div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-4 leading-tight px-4">
              We'd Love to{" "}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Hear From You
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Have questions? We're here to help and answer any questions you might have.
            </p>
          </motion.div>

          {/* Contact Info Cards - Mobile First */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10 sm:mb-12 md:mb-16"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {contactInfo.map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -5, scale: 1.02 }}
                className="relative group"
              >
                <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-blue-200">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${
                    item.color === 'blue' ? 'from-blue-400 to-blue-600' :
                    item.color === 'green' ? 'from-green-400 to-green-600' :
                    item.color === 'purple' ? 'from-purple-400 to-purple-600' :
                    'from-orange-400 to-orange-600'
                  } rounded-xl flex items-center justify-center mb-4 text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {item.icon}
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2 text-center">{item.title}</h3>
                  {item.link ? (
                    <a
                      href={item.link}
                      className="text-sm text-gray-600 hover:text-blue-600 transition-colors block break-words line-clamp-2 text-center"
                      title={item.content}
                    >
                      {item.content}
                    </a>
                  ) : (
                    <p className="text-sm text-gray-600 break-words line-clamp-2 text-center" title={item.content}>
                      {item.content}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Form and Map Container */}
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            
            {/* Contact Form */}
            <motion.div variants={itemVariants}>
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10 relative overflow-hidden border border-gray-100">
                
                {/* Decorative corner gradient */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-blue-100 to-transparent rounded-bl-full opacity-50 pointer-events-none"></div>
                
                <div className="relative z-10">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Send us a Message</h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">Fill out the form below and we'll get back to you shortly.</p>
                  
                  <div className="space-y-5 sm:space-y-6">
                    
                    {/* Name Field */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                      <div className="relative group">
                        <HiOutlineUser 
                          className={`absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors duration-300 ${
                            focusedField === 'name' ? 'text-blue-600' : 'text-gray-400'
                          }`}
                          size={20} 
                        />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          onFocus={() => setFocusedField('name')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="John Doe"
                          className={`w-full pl-12 pr-4 py-3 sm:py-4 border-2 rounded-xl bg-gray-50 focus:bg-white focus:outline-none transition-all duration-300 text-sm sm:text-base ${
                            focusedField === 'name' ? 'border-blue-500 shadow-lg shadow-blue-100' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        />
                      </div>
                    </motion.div>

                    {/* Email Field */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                      <div className="relative group">
                        <HiOutlineMail 
                          className={`absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors duration-300 ${
                            focusedField === 'email' ? 'text-blue-600' : 'text-gray-400'
                          }`}
                          size={20} 
                        />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          onFocus={() => setFocusedField('email')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="john@example.com"
                          className={`w-full pl-12 pr-4 py-3 sm:py-4 border-2 rounded-xl bg-gray-50 focus:bg-white focus:outline-none transition-all duration-300 text-sm sm:text-base ${
                            focusedField === 'email' ? 'border-blue-500 shadow-lg shadow-blue-100' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        />
                      </div>
                    </motion.div>

                    {/* Message Field */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Your Message</label>
                      <div className="relative group">
                        <BiMessageDetail 
                          className={`absolute left-4 top-4 transition-colors duration-300 ${
                            focusedField === 'message' ? 'text-blue-600' : 'text-gray-400'
                          }`}
                          size={20} 
                        />
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          onFocus={() => setFocusedField('message')}
                          onBlur={() => setFocusedField(null)}
                          placeholder="Tell us how we can help you..."
                          rows="5"
                          className={`w-full pl-12 pr-4 py-3 sm:py-4 border-2 rounded-xl bg-gray-50 focus:bg-white focus:outline-none resize-none transition-all duration-300 text-sm sm:text-base ${
                            focusedField === 'message' ? 'border-blue-500 shadow-lg shadow-blue-100' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        ></textarea>
                      </div>
                    </motion.div>

                    {/* Submit Button */}
                    <motion.button
                      onClick={handleSubmit}
                      className="relative w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 sm:py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group text-sm sm:text-base"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={isSubmitted}
                    >
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        {isSubmitted ? (
                          <>
                            <FiCheck size={20} />
                            Message Sent!
                          </>
                        ) : (
                          <>
                            Send Message
                            <FiSend size={18} />
                          </>
                        )}
                      </span>
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600"
                        initial={{ x: "-100%" }}
                        whileHover={{ x: 0 }}
                        transition={{ duration: 0.3 }}
                      />
                    </motion.button>

                    {/* Success Message */}
                    <AnimatePresence>
                      {isSubmitted && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg text-sm"
                        >
                          <FiCheck className="flex-shrink-0" />
                          <span>Thank you! We'll get back to you soon.</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Map Section */}
            <motion.div variants={itemVariants}>
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden h-full min-h-[400px] sm:min-h-[500px] lg:min-h-[600px] border border-gray-100">
                <div className="h-full relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3931.203503579147!2d6.0079231!3d9.0391016!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104966b7f2e9b195%3A0x18c8eec7a2fae37c!2sFederal+Polytechnic+Bida%2C+Doko+Road%2C+Bida%2C+Niger+State!5e0!3m2!1sen!2sus!4v1694784000000!5m2!1sen!2sus"
                    title="Federal Polytechnic Bida Location"
                    className="w-full h-full"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            className="mt-12 sm:mt-16 lg:mt-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 text-white text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">Need Immediate Assistance?</h2>
            <p className="text-base sm:text-lg mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto px-4">
              Our admissions team is ready to answer your questions and guide you through the application process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.a
                href="tel:+2348138644342"
                className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-gray-100 transition-colors shadow-lg text-sm sm:text-base w-full sm:w-auto text-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Call Us Now
              </motion.a>
              <motion.a
                href="/admissions"
                className="px-6 sm:px-8 py-3 sm:py-4 bg-white/20 backdrop-blur-sm text-white font-bold rounded-xl hover:bg-white/30 transition-colors border-2 border-white/30 text-sm sm:text-base w-full sm:w-auto text-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Learn More About Admissions
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;